'use client';

import { Activity, ArrowDownRight, ArrowUpRight, ArrowUpDown } from 'lucide-react';
import { cn } from '@/lib/utils';
import { badgeTrendSymbol } from '@/lib/price-activity';
import type { PriceActivityCategorySummary } from '@/lib/price-activity-types';

type CategoryPriceActivityBadgeProps = {
  summary: PriceActivityCategorySummary;
  categoryLabel: string;
  onOpen: () => void;
  className?: string;
};

function TrendIcon({
  trend,
}: {
  trend: PriceActivityCategorySummary['trend'];
}) {
  if (trend === 'up') {
    return <ArrowUpRight className="h-3 w-3 text-emerald-200" aria-hidden />;
  }
  if (trend === 'down') {
    return <ArrowDownRight className="h-3 w-3 text-rose-200" aria-hidden />;
  }
  if (trend === 'mixed') {
    return <ArrowUpDown className="h-3 w-3 text-sky-100" aria-hidden />;
  }
  return <Activity className="h-3 w-3 text-white/80" aria-hidden />;
}

export function CategoryPriceActivityBadge({
  summary,
  categoryLabel,
  onOpen,
  className,
}: CategoryPriceActivityBadgeProps) {
  const count = summary.total;
  const symbol = badgeTrendSymbol(summary.trend);
  const label =
    count === 0
      ? `Sem alterações recentes em ${categoryLabel}. Abrir actividade de preços.`
      : `${count} alteração${count === 1 ? '' : 'ões'} recente${count === 1 ? '' : 's'} em ${categoryLabel} (${symbol}). Abrir actividade de preços.`;

  return (
    <button
      type="button"
      onClick={(event) => {
        event.preventDefault();
        event.stopPropagation();
        onOpen();
      }}
      onPointerDown={(event) => {
        event.stopPropagation();
      }}
      aria-label={label}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 rounded-full border border-solid px-2 py-0.5',
        'border-white/25 bg-black/25 font-poppins text-[11px] font-bold tabular-nums text-white',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.12)] backdrop-blur-[2px]',
        'transition-[background-color,border-color,box-shadow,transform] duration-200',
        'hover:border-white/45 hover:bg-black/40 hover:shadow-[0_0_12px_rgba(56,189,248,0.18)]',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-sky-300/60',
        'active:scale-[0.97]',
        className
      )}
    >
      <TrendIcon trend={summary.trend} />
      <span>{count}</span>
    </button>
  );
}
